/**
 * Orchestrates frame lifecycles: it stands up the call iframe, opens the PiP window and pip
 * iframe on request, brokers a private MessageChannel between them, and tears everything down.
 */
import { CallFrame } from './call-frame';
import { resolveFrames, type ResolvedFrames } from './config';
import { logger } from './logger';
import { PipController } from './pip-window';
import {
	isFromFrameMessage,
	PROTOCOL_VERSION,
	type CallEndReason,
	type CallOpenPayload,
	type ControlMessage
} from './protocol';
import type { CallErrorEvent, EventHandlers, OpenArgs, OpenOptions } from './types';

const CLOSE_TEARDOWN_TIMEOUT_MS = 1500;
const FRAME_LOAD_TIMEOUT_MS = 15_000;
const REVEAL_FALLBACK_MS = 10_000;

type PackageEndReason = 'integrator' | 'detached' | 'error' | 'user';

const UNTRANSFERABLE_ARGS_ERROR = 'open-args-not-transferable';

export class Orchestrator {
	private handlers: Partial<EventHandlers> = {};

	private frameEndReason: CallEndReason | null = null;
	private errored = false;
	private integratorClosed = false;
	private detached = false;

	private callFrame: CallFrame | null = null;
	private pip: PipController | null = null;
	private frames: ResolvedFrames | null = null;
	private currentAgentId: string | null = null;
	private pendingOpen: OpenArgs | null = null;

	private callReady = false;
	private pipReady = false;
	private pipEnabled = true;
	private pipOpening = false; // is a `requestWindow()` is in flight.
	private expectedPipClose = false;
	private windowListener: ((event: MessageEvent) => void) | null = null;
	private pipUnsubscribers: Array<() => void> = [];
	private teardownWatchers: Array<() => void> = [];
	private closeTimer: ReturnType<typeof setTimeout> | null = null;
	private frameLoadListener: (() => void) | null = null;
	private frameLoaded = false;
	private loadTimer: ReturnType<typeof setTimeout> | null = null;
	private revealTimer: ReturnType<typeof setTimeout> | null = null;

	/** Decides restart / refresh / fresh session. Standing a session up lives in `startSession`. */
	open(args: OpenArgs, options: OpenOptions = {}): void {
		if (this.closeTimer) this.teardown();

		if (this.callFrame) {
			// Same agent while already live: no new session, but adopt the callbacks just passed.
			if (this.currentAgentId === args.agentId) {
				this.handlers = { ...args.on };
				return;
			}
			this.integratorClosed = true;
			this.teardown();
		}

		this.startSession(args, options);
	}

	private startSession(args: OpenArgs, options: OpenOptions): void {
		const frames = resolveFrames(args.agentId, options);
		this.frames = frames;
		this.currentAgentId = args.agentId;
		this.pendingOpen = args;
		this.handlers = { ...args.on };
		this.callReady = false;
		this.frameLoaded = false;
		this.pipEnabled = options.pip !== false;

		this.callFrame = new CallFrame(frames.callUrl, frames.origin);

		this.windowListener = (event: MessageEvent) => {
			if (!this.callFrame?.isFromFrame(event)) return;
			if (isFromFrameMessage(event.data)) this.handleCallMessage(event.data);
		};
		window.addEventListener('message', this.windowListener);

		this.watchForAbruptTeardown();
		this.armFrameLoadDeadline(this.callFrame.iframe);
	}

	private armFrameLoadDeadline(iframe: HTMLIFrameElement): void {
		this.loadTimer = setTimeout(() => this.abandonFrame(), FRAME_LOAD_TIMEOUT_MS);

		const onLoad = (): void => {
			this.frameLoadListener = null;
			this.frameLoaded = true;
		};
		// `once`, or a later navigation would fire this again.
		iframe.addEventListener('load', onLoad, { once: true });
		this.frameLoadListener = () => iframe.removeEventListener('load', onLoad);
	}

	private clearLoadTimer(): void {
		this.frameLoadListener?.();
		this.frameLoadListener = null;
		if (!this.loadTimer) return;
		clearTimeout(this.loadTimer);
		this.loadTimer = null;
	}

	private clearRevealTimer(): void {
		if (!this.revealTimer) return;
		clearTimeout(this.revealTimer);
		this.revealTimer = null;
	}

	private abandonFrame(): void {
		this.loadTimer = null;
		this.frameLoadListener?.();
		this.frameLoadListener = null;
		// Raced by a teardown, or a late qz:ready already cleared it.
		if (!this.callFrame || this.callReady) return;
		this.errored = true;
		this.reportError({
			code: this.frameLoaded ? 'frame-not-ready' : 'frame-unreachable',
			fatal: true
		});
		this.teardown();
	}

	/**
	 * Add listeners to handle the two ways a session ends without the frame saying so:
	 * an SPA unmounting our container, and a full page navigation.
	 */
	private watchForAbruptTeardown(): void {
		const container = this.callFrame?.container;
		const parent = container?.parentNode;
		if (container && parent) {
			const observer = new MutationObserver(() => {
				if (container.isConnected) return;
				this.detached = true;
				this.teardown();
			});
			observer.observe(parent, { childList: true });
			this.teardownWatchers.push(() => observer.disconnect());
		}

		const onPageHide = () => {
			this.detached = true;
			this.teardown();
		};
		window.addEventListener('pagehide', onPageHide);
		this.teardownWatchers.push(() => window.removeEventListener('pagehide', onPageHide));
	}

	close(): void {
		if (!this.callFrame) return;
		this.integratorClosed = true;
		this.callFrame.post({ v: PROTOCOL_VERSION, kind: 'qz:close' });
		this.closeTimer ??= setTimeout(() => this.teardown(), CLOSE_TEARDOWN_TIMEOUT_MS);
	}

	/** Must be called synchronously inside a real user gesture: `requestWindow()` needs transient activation. */
	openPip(): void {
		void this.openPipWindow();
	}

	/** Close the floating window, leaving the call running in the on-page frame. No-op if none. */
	closePip(): void {
		this.closePipWindow();
	}

	// --- message handlers -----------------------------------------------------------------------

	private handleCallMessage(message: ControlMessage): void {
		switch (message.kind) {
			case 'qz:ready':
				if (message.role !== 'call') return;
				this.callReady = true;
				this.clearLoadTimer();
				if (this.closeTimer) {
					this.callFrame?.post({ v: PROTOCOL_VERSION, kind: 'qz:close' });
					break;
				}
				this.revealTimer = setTimeout(() => {
					logger.warn('Frame did not report itself presentable; opening the panel anyway.');
					this.callFrame?.reveal();
				}, REVEAL_FALLBACK_MS);
				if (this.pendingOpen) this.postOpen(this.pendingOpen);
				this.wireChannel();
				break;
			case 'qz:resize':
				this.callFrame?.resize(message.width, message.height, message.radius, message.durationMs);
				break;
			case 'qz:reveal':
				this.clearRevealTimer();
				this.callFrame?.reveal();
				break;
			case 'qz:drag':
				// The frame owns the gesture, so its relayed displacement is what moves the panel.
				if (message.phase === 'start') this.callFrame?.beginDrag();
				else if (message.phase === 'end') this.callFrame?.endDrag();
				else this.callFrame?.dragBy(message.dx, message.dy);
				break;
			case 'qz:call-started':
				this.emit('callStarted');
				break;
			case 'qz:call-ended':
				this.emit('callEnded');
				break;
			case 'qz:error':
				if (message.fatal) this.errored = true;
				this.reportError({ code: message.code, fatal: message.fatal });
				break;
			case 'qz:end':
				this.frameEndReason ??= message.reason;
				this.teardown();
				break;
			case 'qz:pip':
				if (message.open) void this.openPipWindow();
				else this.closePipWindow();
				break;
		}
	}

	private handlePipMessage(message: ControlMessage): void {
		if (message.kind === 'qz:ready' && message.role === 'pip') {
			this.pipReady = true;
			this.pip?.dismissPlaceholder();
			this.wireChannel();
		}
	}

	private handlePipClosed(): void {
		const userInitiated = !this.expectedPipClose;
		this.tearDownPip();
		this.callFrame?.post({ v: PROTOCOL_VERSION, kind: 'qz:pip-closed', userInitiated });
	}

	private emit<K extends keyof EventHandlers>(
		...args: Parameters<EventHandlers[K]> extends []
			? [event: K]
			: [event: K, payload: Parameters<EventHandlers[K]>[0]]
	): void {
		const [event, payload] = args as [K, unknown];
		const handler = this.handlers[event];
		if (!handler) return;
		try {
			(handler as (payload: unknown) => void)(payload);
		} catch (err) {
			logger.error('callback threw:', event, err);
		}
	}

	private reportError(event: CallErrorEvent): void {
		this.emit('error', event);
	}

	private postOpen(args: OpenArgs): void {
		const { agentId, workspaceToken, user, context, locale } = args;
		const payload: CallOpenPayload = { agentId, workspaceToken, user, context, locale };

		try {
			this.callFrame?.post({ v: PROTOCOL_VERSION, kind: 'qz:open', args: payload });
		} catch (err) {
			logger.error('open() arguments could not be transferred to the frame', err);
			this.errored = true;
			this.reportError({ code: UNTRANSFERABLE_ARGS_ERROR, fatal: true });
			this.teardown();
		}
	}

	// --- PiP ------------------------------------------------------------------------------------

	private async openPipWindow(): Promise<void> {
		if (!this.pipEnabled || this.pip || this.pipOpening || !this.callFrame || !this.frames) return;
		const agentId = this.currentAgentId;

		this.pipOpening = true;
		let pip: PipController | null;
		try {
			pip = await PipController.open(this.frames.pipUrl, this.frames.origin);
		} finally {
			this.pipOpening = false;
		}
		if (!pip) return; // unsupported / denied

		// A close(), agent switch or a race winner may have landed while this was resolving.
		if (this.pip || !this.callFrame || this.currentAgentId !== agentId) {
			pip.destroy();
			return;
		}

		this.pip = pip;
		this.pipReady = false;
		this.expectedPipClose = false;
		this.pipUnsubscribers.push(
			pip.onControlMessage((message) => this.handlePipMessage(message)),
			pip.onClose(() => this.handlePipClosed()),
			this.closePipOnHostRefocus()
		);
	}

	/**
	 * Returning to the host tab closes the PiP & the on-page frame becomes visible again.
	 * Returns an unsubscribe fn.
	 */
	private closePipOnHostRefocus(): () => void {
		const onVisibilityChange = () => {
			if (document.visibilityState === 'visible') this.closePipWindow();
		};
		document.addEventListener('visibilitychange', onVisibilityChange);
		return () => document.removeEventListener('visibilitychange', onVisibilityChange);
	}

	private closePipWindow(): void {
		if (!this.pip) return;
		this.expectedPipClose = true;
		this.pip.destroy();
	}

	// --- channel --------------------------------------------------------------------------------

	/**
	 * Broker a fresh private channel between the two iframes. Gated on both handshakes: a `qz:port`
	 * posted before a frame attached its listener is dropped, since postMessage does not buffer. Ports
	 * are single-use, so each pip session gets its own channel.
	 */
	private wireChannel(): void {
		if (!this.callReady || !this.pipReady || !this.callFrame || !this.pip) return;
		const channel = new MessageChannel();
		this.callFrame.post({ v: PROTOCOL_VERSION, kind: 'qz:port' }, [channel.port1]);
		this.pip.post({ v: PROTOCOL_VERSION, kind: 'qz:port' }, [channel.port2]);
	}

	// --- teardown -------------------------------------------------------------------------------

	private tearDownPip(): void {
		for (const unsub of this.pipUnsubscribers) unsub();
		this.pipUnsubscribers = [];
		this.pip?.destroy();
		this.pip = null;
		this.pipReady = false;
		this.expectedPipClose = false;
	}

	private teardown(): void {
		const hadSession = this.callFrame !== null;
		const fallback: PackageEndReason = this.errored
			? 'error'
			: this.integratorClosed
				? 'integrator'
				: this.detached
					? 'detached'
					: 'user';
		const reason: CallEndReason = this.frameEndReason ?? fallback;
		const handlers = this.handlers;
		this.frameEndReason = null;
		this.errored = false;
		this.integratorClosed = false;
		this.detached = false;

		if (this.closeTimer) {
			clearTimeout(this.closeTimer);
			this.closeTimer = null;
		}
		this.clearLoadTimer();
		this.clearRevealTimer();
		if (this.windowListener) {
			window.removeEventListener('message', this.windowListener);
			this.windowListener = null;
		}
		for (const detach of this.teardownWatchers) detach();
		this.teardownWatchers = [];
		this.tearDownPip();
		this.callFrame?.destroy();
		this.callFrame = null;
		this.callReady = false;
		this.pendingOpen = null;
		this.currentAgentId = null;
		this.frames = null;
		this.handlers = {};

		if (hadSession && handlers.close) {
			try {
				handlers.close({ reason });
			} catch (err) {
				logger.error('callback threw:', 'close', err);
			}
		}
	}
}
